import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, ArrowDownTrayIcon, ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { fairLendingApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import RiskBadge from '../components/fair-lending/RiskBadge';
import DenialRateChart from '../components/fair-lending/DenialRateChart';
import PricingChart from '../components/fair-lending/PricingChart';
import GeographicTable from '../components/fair-lending/GeographicTable';
import FindingCard from '../components/fair-lending/FindingCard';
import clsx from 'clsx';

type Tab = 'denial_rate' | 'pricing' | 'geographic';

export default function FairLendingAnalysisPage() {
  const { id } = useParams<{ id: string }>();
  const { teamId } = useTeamId();
  const [activeTab, setActiveTab] = useState<Tab>('denial_rate');
  const [exporting, setExporting] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['fair-lending-analysis', teamId, id],
    queryFn: async () => {
      const res = await fairLendingApi.getAnalysis(teamId!, id!);
      return res.data.analysis;
    },
    enabled: !!id && !!teamId,
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-96 bg-gray-200 rounded-lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load analysis. It may not exist or you may not have access.</p>
        <Link to="/fair-lending" className="mt-4 text-sm text-ocean-600 hover:underline">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const result = data.resultJson as any;
  const analysisTypes: string[] = data.analysisTypes || [];

  // Build tabs based on what was analyzed
  const tabs: { key: Tab; label: string }[] = [];
  if (analysisTypes.includes('DENIAL_RATE')) tabs.push({ key: 'denial_rate', label: 'Denial Rates' });
  if (analysisTypes.includes('PRICING')) tabs.push({ key: 'pricing', label: 'Pricing' });
  if (analysisTypes.includes('GEOGRAPHIC')) tabs.push({ key: 'geographic', label: 'Geographic' });
  // CONTENT_SCREENING moved to marketing compliance pipeline (Sprint 0.4)

  // Set default tab to first available
  if (tabs.length > 0 && !tabs.find(t => t.key === activeTab)) {
    setActiveTab(tabs[0].key);
  }

  const findings = data.findings || [];
  const nonLowFindings = findings.filter((f: any) => f.riskLevel !== 'LOW');

  const handleExportPdf = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const response = await fairLendingApi.exportPdf(teamId!, id!);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fair-lending-report-${Date.now()}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch {
      toast.error('PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link to="/fair-lending" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Dashboard
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Fair Lending Analysis
            </h1>
            <div className="mt-1 flex items-center gap-4 text-sm text-gray-600">
              <span>{data.validation?.fileName}</span>
              {data.validation?.institutionName && (
                <span>{data.validation.institutionName}</span>
              )}
              <span>{new Date(data.createdAt).toLocaleDateString()}</span>
              {data.durationMs && (
                <span className="flex items-center gap-1">
                  <ClockIcon className="h-3.5 w-3.5" />
                  {(data.durationMs / 1000).toFixed(1)}s
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportPdf}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {exporting ? 'Exporting...' : 'Export PDF'}
            </button>
            <RiskBadge level={data.overallRiskLevel} className="text-sm" />
          </div>
        </div>
      </div>

      {/* Limitations banner */}
      {result?.limitations?.length > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Data Limitations</p>
              {result.limitations.map((l: string, i: number) => (
                <p key={i} className="text-sm text-amber-700 mt-1">{l}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      {tabs.length > 0 && (
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-6">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={clsx(
                  'pb-3 text-sm font-medium border-b-2 transition-colors',
                  activeTab === tab.key
                    ? 'border-ocean-500 text-ocean-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Tab content */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        {activeTab === 'denial_rate' && result?.denialRateResults && (
          <DenialRateChart results={result.denialRateResults} />
        )}
        {activeTab === 'pricing' && result?.pricingResults && (
          <PricingChart results={result.pricingResults} />
        )}
        {activeTab === 'geographic' && result?.geographicResult && (
          <GeographicTable result={result.geographicResult} />
        )}
      </div>

      {/* Findings section */}
      {nonLowFindings.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Findings ({nonLowFindings.length})
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {nonLowFindings.map((f: any) => (
              <FindingCard key={f.id} finding={f} analysisId={id!} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

